import React, { useEffect, useState } from "react";
import {
  useChangeStatusMutation,
  useCountryCodeQuery,
  useCreateCustomerMutation,
  useCustomersQuery,
  useDeleteCustomerMutation,
  useSingleCustomerQuery,
  useUpdateCustomerMutation,
} from "../../Store/Apis/customerApi";
import Error from "../../Components/Error/Error";
import Loader from "../../Components/Loader/Loader";
import { Table } from "antd";
import { Modal } from "react-bootstrap";
import AddEditCustomerForm from "../../Components/AddEditCustomerForm/AddEditCustomerForm";
import toast from "react-hot-toast";
import { isPermissionGranted } from "../../Hooks/permissionsMiddleware";
import { useSelector } from "react-redux";
import { useOrderByCustomerIdQuery } from "../../Store/Apis/bookingApi";
import { getCompany } from "../../Store/Apis/ReservationApi";

const CompanyControl = () => {
  const user = useSelector((state) => state.auth.user);
  const [sortFilter, setSortFilter] = useState({
    search: "",
    outletID: "",
  });

  const [companyData, setCompanyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch company data in useEffect
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getCompany({
          companyName: sortFilter.search,
          outletID: sortFilter.outletID,
        });
        console.log("Company Data:", result);
        // Normalize response to ensure consistent isActive field
        const toArray = (data) => {
          if (!data) return [];
          if (Array.isArray(data)) return data;
          if (Array.isArray(data?.data)) return data.data;
          return [data];
        };
        const normalize = (item) => ({
          ...item,
          // unify isActive from possible variants (snake_case, pascal, etc.)
          isActive: Number(
            item?.isActive ?? item?.is_active ?? item?.IsActive ?? 0
          ),
        });
        const normalized = toArray(result).map(normalize);
        setCompanyData(normalized);
      } catch (error) {
        console.error("Error fetching company data:", error);
        setError(error);
        setCompanyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [sortFilter.search, sortFilter.outletID]); // Re-fetch when search or outlet changes

  // Keep customer API for fallback, but we'll primarily use company data
  const customerApi = useCustomersQuery({
    keyword: sortFilter.search,
    outletID: sortFilter.outletID,
  });

  const [orderModalShow, setOrderModalShow] = useState(null);
  const oderByCustomerId = useOrderByCustomerIdQuery(orderModalShow, {
    skip: !orderModalShow,
  });

  const [addCustomer, addCustomerResponse] = useCreateCustomerMutation();
  const [addModalShow, setAddModalShow] = useState(false);

  // Listen for external modal open event
  useEffect(() => {
    const handleOpenModal = () => {
      setAddModalShow(true);
    };

    window.addEventListener("openCompanyModal", handleOpenModal);
    return () => {
      window.removeEventListener("openCompanyModal", handleOpenModal);
    };
  }, []);

  const AddCompany = () => {
    const handleFormSubmit = (formData) => {
      // Build minimal body; removed fields per new requirements
      const body = {
        name: formData.customerName,
        email: formData.email,
        countryCode: formData.countryCode.toString(),
        phone: formData.phoneNumber.toString(),
        taxNumber: formData.taxNumber?.toString() || "",
        address: formData.address,
        // Do not send status from form; status changes use dedicated action
        outletID: formData.outletID,
        clientType: "Company", // Force company type
      };

      addCustomer(body)
        .then((res) => {
          if (res?.data?.status === 200) {
            setAddModalShow(false);
            toast.success(res?.data?.message, { duration: 4000 });
            // Refresh company data after successful addition
            const fetchCompanyData = async () => {
              try {
                const result = await getCompany({
                  companyName: sortFilter.search,
                  outletID: sortFilter.outletID,
                });
                setCompanyData(result || []);
              } catch (error) {
                console.error("Error refreshing company data:", error);
              }
            };
            fetchCompanyData();
          } else {
            toast.error(res?.data?.message, { duration: 4000 });
          }
        })
        .catch(() => {
          toast.error("An error occurred. Please try again.", {
            duration: 4000,
          });
        });
    };
    return (
      <>
        <Modal
          show={addModalShow}
          onHide={() => setAddModalShow(false)}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add New Company</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddEditCustomerForm
              handleSubmit={handleFormSubmit}
              isNew={false} // Set to false to avoid showing client type toggle
              customerData={{
                customerName: "",
                email: "",
                phoneNumber: "",
                address: "",
                clientType: "company", // Force company type
                countryCode: "27", // Default South African code
                taxNumber: "",
                isActive: true,
                // Hide individual-specific fields
                salutation: "",
                dateOfBirth: "",
                customerRating: "",
                locality: "",
                pincode: "",
              }}
            />
          </Modal.Body>
        </Modal>
      </>
    );
  };

  // UPDATE COMPANY
  const [updateCustomerId, setUpdateCustomerId] = useState(null);
  const [updateCustomerModalShow, setupdateCustomerModalShow] = useState(false);
  const [editCompanyData, setEditCompanyData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Fetch specific company data when editing
  useEffect(() => {
    if (updateCustomerId && updateCustomerModalShow) {
      const fetchEditCompanyData = async () => {
        try {
          setEditLoading(true);
          const result = await getCompany({
            companyID: updateCustomerId,
            outletID: sortFilter.outletID,
          });
          console.log("Edit Company Data Response:", result);

          // Handle different response formats
          let companyInfo = null;
          if (Array.isArray(result) && result.length > 0) {
            companyInfo = result[0];
          } else if (
            result &&
            Array.isArray(result.data) &&
            result.data.length > 0
          ) {
            companyInfo = result.data[0];
          } else if (result && result.data) {
            companyInfo = result.data;
          }

          // Normalize edit company info
          const normalizedEdit = companyInfo
            ? {
                ...companyInfo,
                isActive: Number(
                  companyInfo?.isActive ??
                    companyInfo?.is_active ??
                    companyInfo?.IsActive ??
                    0
                ),
              }
            : null;
          console.log("Processed Company Info for Edit:", normalizedEdit);
          setEditCompanyData(normalizedEdit);
        } catch (error) {
          console.error("Error fetching company for edit:", error);
          setEditCompanyData(null);
        } finally {
          setEditLoading(false);
        }
      };

      fetchEditCompanyData();
    }
  }, [updateCustomerId, updateCustomerModalShow]);

  const [updateCustomer, updateCustomerResponse] = useUpdateCustomerMutation();

  const UpdateCompany = () => {
    // Debug: Log the actual company data structure
    console.log("Edit Company Data:", editCompanyData);
    console.log("Edit Loading:", editLoading);

    const handleFormSubmit = (formData) => {
      // Add or edit company logic here
      console.log("Form data received from child:", formData);

      const body = {
        id: updateCustomerId,
        salutation: formData.salutation || "N/A",
        name: formData.customerName,
        // Do not auto-fill current or default date; preserve provided or empty
        dateOfBirth: formData.dateOfBirth || "",
        email: formData.email,
        countryCode: formData.countryCode.toString(),
        customerRating: formData.customerRating
          ? parseInt(formData.customerRating)
          : 0,
        phone: formData.phoneNumber.toString(),
        taxNumber: formData.taxNumber || "N/A",
        locality: formData.locality || "N/A",
        pin: formData.pincode || "0000",
        address: formData.address,
        // Do not send status from form; preserve backend status
        outletID: formData.outletID,
        Type: "Company",
      };
      console.log(body);

      updateCustomer(body)
        .then((res) => {
          console.log(res);
          if (res?.data?.status === 200) {
            setupdateCustomerModalShow(false);
            toast.success(res?.data?.message, { duration: 4000 });
            // Refresh company data after successful update
            const fetchCompanyData = async () => {
              try {
                const result = await getCompany({
                  companyName: sortFilter.search,
                  outletID: sortFilter.outletID,
                });
                // Normalize refreshed data as well
                const toArray = (data) => {
                  if (!data) return [];
                  if (Array.isArray(data)) return data;
                  if (Array.isArray(data?.data)) return data.data;
                  return [data];
                };
                const normalize = (item) => ({
                  ...item,
                  isActive: Number(
                    item?.isActive ?? item?.is_active ?? item?.IsActive ?? 0
                  ),
                });
                const normalized = toArray(result).map(normalize);
                setCompanyData(normalized);
              } catch (error) {
                console.error("Error refreshing company data:", error);
              }
            };
            fetchCompanyData();
          } else {
            toast.error(res?.data?.message, { duration: 4000 });
          }
        })
        .catch((err) => {
          toast.error("An error occurred. Please try again.", {
            duration: 4000,
          });
        });
    };

    return (
      <>
        <Modal
          show={updateCustomerModalShow}
          onHide={() => setupdateCustomerModalShow(false)}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>View and Edit Company</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editLoading ? (
              <Loader height="200px" />
            ) : !editCompanyData ? (
              <div className="text-center text-danger p-4">
                <p>Error loading company data. Please try again.</p>
              </div>
            ) : (
              <AddEditCustomerForm
                handleSubmit={handleFormSubmit}
                isNew={false} // Set to false since we're editing existing company data
                customerData={{
                  // Map company data structure to form fields
                  customerName: editCompanyData?.companyName || "",
                  email: editCompanyData?.companyEmailAddress || "",
                  phoneNumber: editCompanyData?.companyPhoneNumber || "",
                  address: editCompanyData?.companyAddress || "",
                  companyID: editCompanyData?.companyID || "",
                  clientType: "company", // This is the key - set clientType to "company"
                  // Set default values for other fields (won't be shown for companies in edit mode)
                  salutation: "",
                  dateOfBirth: "",
                  countryCode: "27", // Default South African code
                  taxNumber: editCompanyData?.taxNumber || "",
                  customerRating: "",
                  locality: "",
                  pincode: "",
                  isActive: editCompanyData?.isActive ?? true,
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      </>
    );
  };

  // STATUS CHANGE
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [changeStatus] = useChangeStatusMutation();
  const handleChangeStatus = () => {
    // Hard guard: prevent status changes while edit modal is open
    if (updateCustomerModalShow) {
      setSelectedStatus(null);
      return;
    }
    setStatusLoading(true);
    changeStatus({
      keyID: selectedStatus.keyID,
      isActive: selectedStatus.isActive,
      Type: "Company",
    })
      .then((res) => {
        console.log(res);
        if (res?.data?.status === 200) {
          // Update the company status in local state immediately
          setCompanyData((prevData) =>
            prevData.map((company) =>
              (company.companyID || company.customerID) === selectedStatus.keyID
                ? { ...company, isActive: selectedStatus.isActive }
                : company
            )
          );
          toast.success(res?.data?.message || "Status updated successfully!", {
            duration: 4000,
          });
        } else {
          toast.error(res?.data?.message || "Failed to update status", {
            duration: 4000,
          });
        }
        setSelectedStatus(null);
        setStatusLoading(false);
      })
      .catch((error) => {
        console.error("Status change error:", error);
        toast.error("An error occurred while updating status", {
          duration: 4000,
        });
        setSelectedStatus(null);
        setStatusLoading(false);
      });
  };

  // DELETE
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteCustomer] = useDeleteCustomerMutation();
  const handleDelete = () => {
    setDeleteLoading(true);
    deleteCustomer({
      keyID: selectedRecord,
      Type: "Company",
    })
      .then((res) => {
        console.log(res);
        if (res?.data?.status === 200) {
          // Remove the deleted company from local state immediately
          setCompanyData((prevData) =>
            prevData.filter(
              (company) =>
                (company.companyID || company.customerID) !== selectedRecord
            )
          );
          toast.success(res?.data?.message || "Company deleted successfully!", {
            duration: 4000,
          });
        } else {
          toast.error(res?.data?.message || "Failed to delete company", {
            duration: 4000,
          });
        }
        setSelectedRecord(null);
        setDeleteLoading(false);
      })
      .catch((error) => {
        console.error("Delete error:", error);
        toast.error("An error occurred while deleting the company", {
          duration: 4000,
        });
        setSelectedRecord(null);
        setDeleteLoading(false);
      });
  };

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setSortFilter({
        ...sortFilter,
        outletID: user.outletID,
      });
    }
  }, []);

  const columns = [
    {
      title: "Company Details",
      render: (i, action) => (
        <>
          <p className="mb-1 text-sm font-weight-bold">
            {action.companyName || action.customerName}
          </p>
          <p className="mb-1 text-sm">
            {action.companyPhoneNumber || action.phoneNumber}
          </p>
          <p className="mb-0 text-xs text-muted">
            {action.companyEmailAddress || action.email}
          </p>
        </>
      ),
    },
    {
      title: "Company Address",
      dataIndex: "companyAddress",
      key: "companyAddress",
      render: (address, record) => (
        <span className="text-sm">{address || record.address || "N/A"}</span>
      ),
    },
    {
      title: "Company ID",
      dataIndex: "companyID",
      key: "companyID",
      render: (companyID, record) => (
        <span className="text-sm">
          {companyID || record.customerID || "N/A"}
        </span>
      ),
    },
    {
      title: "Order History",
      render: (i, action) => (
        <>
          <button
            type="button"
            className={`btn badge badge-primary badge-xs fw-600 text-xs mb-0`}
            onClick={() =>
              setOrderModalShow(action.companyID || action.customerID)
            }
          >
            View Orders
          </button>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (i, action) => (
        <button
          type="button"
          className={`btn badge badge-xs ${
            Number(
              action.isActive ?? action.is_active ?? action.IsActive ?? 0
            ) === 1
              ? "badge-success"
              : "badge-danger"
          } fw-600 text-xs mb-0`}
          disabled={updateCustomerModalShow}
          onClick={() =>
            setSelectedStatus({
              keyID: action.companyID || action.customerID,
              isActive:
                Number(
                  action.isActive ?? action.is_active ?? action.IsActive ?? 0
                ) === 1
                  ? 0
                  : 1,
            })
          }
        >
          {Number(
            action.isActive ?? action.is_active ?? action.IsActive ?? 0
          ) === 1
            ? "Active"
            : "Deactivated"}
        </button>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (i, action) =>
        isPermissionGranted(20) && (
          <>
            <button
              type="button"
              className="btn badge badge-xs badge-warning fw-600 text-xs mb-0"
              onClick={() => (
                setSelectedStatus(null),
                setUpdateCustomerId(action.companyID || action.customerID),
                setupdateCustomerModalShow(true)
              )}
            >
              View & Edit
            </button>

            <button
              type="button"
              className="btn ms-2 badge badge-xs badge-danger text-xs fw-600 mb-0"
              onClick={() =>
                setSelectedRecord(action.companyID || action.customerID)
              }
              disabled={action.hasOrder === 1}
            >
              Delete
            </button>
          </>
        ),
    },
  ];

  return (
    <>
      <div className="row align-items-center justify-content-between mb-4">
        {/* <div className="col">
          <h5 className="fw-500 text-white">Company Management</h5>
        </div> */}
        {/* <div className="col-auto">
          {isPermissionGranted(20) && (
            <a
              button="button"
              className="btn btn-icon btn-3 btn-white text-primary mb-1 mx-1"
              onClick={() => setAddModalShow(true)}
            >
              <i className="fa fa-plus me-2" /> Add New Company
            </a>
          )}
        </div> */}
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header p-4">
              <div className="row">
                <div className="col-md-12 mb-1">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by Company Name, Email and Phone"
                    value={sortFilter?.search || ""}
                    onChange={(e) =>
                      setSortFilter({ ...sortFilter, search: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {error && <Error />}
              {loading && <Loader height="500px" />}
              {!loading && !error && (
                <>
                  <Table
                    columns={columns}
                    dataSource={companyData}
                    rowKey={(record) =>
                      record.companyID || record.id || Math.random()
                    }
                  />
                </>
              )}
              <AddCompany />
              <UpdateCompany />
            </div>
          </div>
        </div>
      </div>

      {/* STATUS CHANGE MODAL */}
      <Modal
        show={selectedStatus}
        onHide={() => !statusLoading && setSelectedStatus(null)}
        size="sm"
        centered
      >
        <Modal.Body className="text-center p-4">
          {statusLoading ? (
            <>
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
              <h6>Updating status...</h6>
              <p className="text-muted mb-0">Please wait</p>
            </>
          ) : (
            <>
              <h5>Are you sure you want to change company status?</h5>
              <div className="buttons d-flex gap-3 justify-content-center">
                <button
                  className="btn btn-danger bg-danger btn-sm mb-0"
                  onClick={() => setSelectedStatus(null)}
                  disabled={statusLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm mb-0"
                  onClick={handleChangeStatus}
                  disabled={statusLoading}
                >
                  Confirm
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        show={selectedRecord}
        onHide={() => !deleteLoading && setSelectedRecord(null)}
        size="sm"
        centered
      >
        <Modal.Body className="text-center p-4">
          {deleteLoading ? (
            <>
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
              <h6>Deleting company...</h6>
              <p className="text-muted mb-0">Please wait</p>
            </>
          ) : (
            <>
              <h5>Are you sure you want to delete this company?</h5>
              <div className="buttons d-flex gap-3 justify-content-center">
                <button
                  className="btn btn-danger bg-danger btn-sm mb-0"
                  onClick={() => setSelectedRecord(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm mb-0"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  Confirm
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* VIEW ORDERS MODAL */}
      <Modal
        show={orderModalShow}
        onHide={() => setOrderModalShow(null)}
        size="md"
        centered
      >
        <Modal.Header>
          <Modal.Title>Company Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {oderByCustomerId.isLoading || oderByCustomerId.isFetching ? (
            <Loader height="300px" />
          ) : oderByCustomerId.isError ? (
            <Loader height="300px" />
          ) : (
            <div>
              {oderByCustomerId.isSuccess &&
                (oderByCustomerId.data.data.length > 0 ? (
                  oderByCustomerId.data.data.map((elem, i) => (
                    <div key={i}>
                      <p className="fw-bold text-sm text-dark mb-0">
                        Order Id - {elem.orderId}
                      </p>
                      <p className="text-xs text-dark mb-1">
                        Date - {elem.orderDate.split("T")[0]}
                      </p>
                      <p className="text-xs text-dark mb-2">
                        Total - R{" "}
                        {elem.serviceTypeAndName.reduce((acc, it) => {
                          return acc + it.rate;
                        }, 0)}
                      </p>
                      <div className="row mb-3 g-2">
                        {elem.serviceTypeAndName.map((service, index) => (
                          <div className="col-lg-4 col-md-4 col-3" key={index}>
                            <div className="border-dashed border-1 border-secondary border-radius-md position-relative p-2 h-100 d-block mb-0 m-0">
                              <span className="text-sm font-weight-bold mb-1 d-block text-dark">
                                {service.productServiceName}
                              </span>
                              <span className="text-xs font-weight-bold mb-1 d-block">
                                {service.serviceName}
                              </span>
                              <span
                                className="d-block text-danger"
                                style={{ fontSize: "12px", marginTop: "0px" }}
                              >
                                Rate: {service.rate}
                              </span>
                              <span
                                className="d-block text-success"
                                style={{ fontSize: "12px", marginTop: "0px" }}
                              >
                                Quantity: {service.quantity}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-center my-5">
                    No Data found !!
                  </div>
                ))}
            </div>
          )}
          <div className="buttons d-flex gap-3 justify-content-center">
            <button
              className="btn btn-danger bg-danger btn-sm mb-0"
              onClick={() => setOrderModalShow(null)}
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CompanyControl;
